import React, { createElement, useCallback, useEffect, useRef, useState } from 'react';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import {
  Box,
  Button,
  FormControlLabel,
  GridLegacy as Grid,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DateRangePicker } from '@mui/x-date-pickers-pro';

import { getRegionNamesAndCode } from '@luxuryescapes/lib-regions';

import { sources } from '~/components/Accommodation/constants';
import lazyImport from '~/components/utils/lazyImport';

import useCurrentBrand from '~/hooks/useCurrentBrand';

import BedbankService from '~/services/BedbankService';
import { Dayjs, formatDateISO } from '~/services/TimeService';

import DownloadRawRates from './DownloadRawRates';

dayjs.extend(customParseFormat);

interface RateInfoProps {
  propertyId: string;
}

const today = dayjs();

function parseQueryParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    source: params.get('source'),
    checkIn: params.get('check_in'),
    checkOut: params.get('check_out'),
    region: params.get('region'),
    adults: params.get('adults'),
    children: params.get('children'),
    allRates: params.get('allRates') === 'true',
    enablePropertyCollect: params.get('enablePropertyCollect') === 'true',
    showFlightBundle: params.get('showFlightBundle') !== 'false',
  };
}

function RateInfo({ propertyId }: RateInfoProps) {
  const queryParams = parseQueryParams();
  const hasInitialParams = !!(queryParams.checkIn && queryParams.checkOut);

  const [checkIn, setCheckIn] = useState<Dayjs | null>(
    queryParams.checkIn ? dayjs(queryParams.checkIn, 'DD-MM-YYYY') : null,
  );
  const [checkOut, setCheckOut] = useState<Dayjs | null>(
    queryParams.checkOut ? dayjs(queryParams.checkOut, 'DD-MM-YYYY') : null,
  );
  const [region, setRegion] = useState(queryParams.region || 'AU');
  const brand = useCurrentBrand();
  const [adults, setAdults] = useState(queryParams.adults || '2');
  const [children, setChildren] = useState<string | null>(queryParams.children || null);
  const [hasError, setHasError] = useState(true);
  const [source, setSource] = useState(queryParams.source || null);
  const [allRates, setAllRates] = useState(queryParams.allRates);
  const [enablePropertyCollect, setEnablePropertyCollect] = useState(queryParams.enablePropertyCollect);
  const [showFlightBundle, setShowFlightBundle] = useState(queryParams.showFlightBundle);
  const [errorMessage, setHasErrorMessage] = useState('');
  const [fetchReqState, setFetchReqState] = useState<{
    status: number | string;
    error?: Error;
    result?: App.Bedbank.AdminRatesForProperty;
  }>(null);
  const autoSubmitTriggered = useRef(false);

  const updateQueryParams = useCallback(
    (
      updates: Partial<{
        checkIn: Dayjs | null;
        checkOut: Dayjs | null;
        region: string;
        adults: string;
        children: string | null;
        source: string | null;
        allRates: boolean;
        enablePropertyCollect: boolean;
        showFlightBundle: boolean;
      }>,
    ) => {
      const params = new URLSearchParams(window.location.search);

      if (updates.checkIn !== undefined) {
        if (updates.checkIn) {
          params.set('check_in', updates.checkIn.format('DD-MM-YYYY'));
        } else {
          params.delete('check_in');
        }
      }

      if (updates.checkOut !== undefined) {
        if (updates.checkOut) {
          params.set('check_out', updates.checkOut.format('DD-MM-YYYY'));
        } else {
          params.delete('check_out');
        }
      }

      if (updates.region !== undefined) {
        params.set('region', updates.region);
      }

      if (updates.adults !== undefined) {
        params.set('adults', updates.adults);
      }

      if (updates.children !== undefined) {
        if (updates.children) {
          params.set('children', updates.children);
        } else {
          params.delete('children');
        }
      }

      if (updates.source !== undefined) {
        if (updates.source) {
          params.set('source', updates.source);
        } else {
          params.delete('source');
        }
      }

      if (updates.allRates !== undefined) {
        if (updates.allRates) {
          params.set('allRates', 'true');
        } else {
          params.delete('allRates');
        }
      }

      if (updates.enablePropertyCollect !== undefined) {
        if (updates.enablePropertyCollect) {
          params.set('enablePropertyCollect', 'true');
        } else {
          params.delete('enablePropertyCollect');
        }
      }

      if (updates.showFlightBundle !== undefined) {
        if (!updates.showFlightBundle) {
          params.set('showFlightBundle', 'false');
        } else {
          params.delete('showFlightBundle');
        }
      }

      const newUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
      window.history.replaceState({}, '', newUrl);
    },
    [],
  );

  function validate() {
    if (!checkIn || !checkOut || checkOut.isBefore(checkIn)) {
      setHasError(true);
      return;
    }
    setHasError(false);
  }

  function handleRegionChange(newRegion: string) {
    setRegion(newRegion);
    updateQueryParams({ region: newRegion });
  }

  function handleAdultsChange(newAdults: string) {
    setAdults(newAdults);
    updateQueryParams({ adults: newAdults });
  }

  function handleChildrenChange(newChildren: string) {
    const value = newChildren || null;
    setChildren(value);
    updateQueryParams({ children: value });
  }

  function handleSourceChange(newSource: string) {
    setSource(newSource);
    updateQueryParams({ source: newSource });
  }

  const getRates = useCallback(async () => {
    const updatedCheckIn = formatDateISO(checkIn.toISOString());
    const updatedCheckOut = formatDateISO(checkOut.toISOString());
    return await BedbankService.getRateInfo({
      checkIn: updatedCheckIn,
      checkOut: updatedCheckOut,
      propertyId,
      region,
      brand,
      adults,
      children,
      source,
      allRates,
      enablePropertyCollect,
    });
  }, [checkIn, checkOut, propertyId, region, brand, adults, children, source, allRates, enablePropertyCollect]);

  async function onSubmit() {
    if (hasError) {
      setHasErrorMessage('Please select valid dates');
      return;
    }
    setFetchReqState({ status: 'pending' });
    try {
      const resp = await getRates();
      const updatedResp = resp.result.rooms.map((room) => {
        const updatedCancellationPolicy = room?.refundable ? 'RF' : 'NRF';
        const updatedGroupId = `${room.boardCode}-${updatedCancellationPolicy}`;
        return {
          ...room,
          cancellationPolicies: updatedCancellationPolicy,
          groupId: updatedGroupId,
        };
      });
      resp.result.rooms = updatedResp;
      setFetchReqState(resp);
    } catch (e) {
      setFetchReqState({ status: 'failed', error: e.message });
    }
  }

  useEffect(() => {
    validate();
  }, [checkIn, checkOut]);

  // Auto-submit when query params are present and valid
  useEffect(() => {
    if (hasInitialParams && !hasError && !autoSubmitTriggered.current && checkIn && checkOut) {
      autoSubmitTriggered.current = true;
      onSubmit();
    }
  }, [hasError, hasInitialParams, checkIn, checkOut]);

  return (
    <>
      <Grid display="flex" flexDirection="row" justifyContent="space-evenly">
        <Grid display="flex" flexDirection="row" alignItems="center" gap="1rem">
          <DateRangePicker
            localeText={{ start: 'Check In', end: 'Check Out' }}
            value={[checkIn, checkOut]}
            onChange={([newCheckIn, newCheckOut]) => {
              setCheckIn(newCheckIn);
              setCheckOut(newCheckOut);
              updateQueryParams({ checkIn: newCheckIn, checkOut: newCheckOut });
            }}
            minDate={today}
          />
        </Grid>
        <Grid display="flex" flexDirection="row" alignItems="center" gap="1rem" mx="0.3rem">
          <Typography fontWeight="bold">Region</Typography>
          <Select value={region} onChange={(e) => handleRegionChange(e.target.value as string)} size="small">
            {getRegionNamesAndCode().map((r) => (
              <MenuItem key={r.code} value={r.code}>
                {r.name}
              </MenuItem>
            ))}
          </Select>
          <Typography fontWeight="bold">Source</Typography>
          <Select value={source} onChange={(e) => handleSourceChange(e.target.value)} size="small">
            <MenuItem key="all" value={null}>
              &nbsp;
            </MenuItem>
            {sources.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid display="flex" flexDirection="row" alignItems="center" gap="1rem">
          <Typography fontWeight="bold">Adults</Typography>
          <TextField type="number" value={adults} onChange={(e) => handleAdultsChange(e.target.value)} size="small" />
        </Grid>
        <Grid display="flex" flexDirection="row" alignItems="center" gap="1rem">
          <Typography fontWeight="bold">Children ages</Typography>
          <TextField
            type="text"
            value={children ?? ''}
            onChange={(e) => handleChildrenChange(e.target.value)}
            size="small"
          />
        </Grid>
        {errorMessage && <Typography color="error">{errorMessage}</Typography>}
      </Grid>
      <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center" gap={3} mt={2}>
        <FormControlLabel
          control={
            <Switch
              checked={allRates}
              onChange={(e) => {
                setAllRates(e.target.checked);
                updateQueryParams({ allRates: e.target.checked });
              }}
              size="small"
            />
          }
          label="All Rates"
        />
        <FormControlLabel
          control={
            <Switch
              checked={enablePropertyCollect}
              onChange={(e) => {
                setEnablePropertyCollect(e.target.checked);
                updateQueryParams({ enablePropertyCollect: e.target.checked });
              }}
              size="small"
            />
          }
          label="Property Collect"
        />
        <FormControlLabel
          control={
            <Switch
              checked={showFlightBundle}
              onChange={(e) => {
                setShowFlightBundle(e.target.checked);
                updateQueryParams({ showFlightBundle: e.target.checked });
              }}
              size="small"
            />
          }
          label="Flight Bundle"
        />
      </Box>
      <Grid display="flex" flexDirection="row" justifyContent="space-evenly" mt={2}>
        <div className="button-container">
          <Button variant="contained" color="primary" disabled={hasError} onClick={onSubmit}>
            Get Rates
          </Button>
        </div>
        <DownloadRawRates rawlogs={fetchReqState?.result?.rawRateLogs} />
      </Grid>
      {fetchReqState &&
        createElement(
          lazyImport(() => import('./RateInfoResultsTable')),
          { fetchReqState, showFlightBundle, showPropertyCollect: enablePropertyCollect },
        )}
    </>
  );
}

export default RateInfo;
